import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { PillView, useAction } from "../../components/ui";
import { money } from "../../lib/format";
import type { CInvoice } from "./data";
import { feeRest, pill } from "./model";
import { dateOf, useLang, useT } from "./sprache";
import { openDocument } from "./ui";
import { reportTransfer } from "./actions";

/**
 * Provisionsrechnungen der Agentur an den Creator bei direkter Abrechnung (Befund 14): der Creator zahlt, also eine
 * Aufgabe mit Frist, Betrag, Bankverbindung der Agentur und Verwendungszweck. Die Bankverbindung liefert
 * my_agency_payment_details (0058) nur, wenn eine solche Rechnung an den Creator versendet ist.
 */

export interface AgencyAccount {
  currency: string;
  holder: string;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  is_default: boolean;
}

export function useAgencyAccounts(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["creator-agency-accounts", orgId],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: () => api.read<AgencyAccount[]>("my_agency_payment_details", { p_org: orgId }).catch(() => [] as AgencyAccount[]),
  });
}

/** Offen: versendet, nicht bezahlt. */
export const feeOpen = (i: Pick<CInvoice, "status" | "paid_at">): boolean => i.status === "sent" && !i.paid_at;

export function feeDue(i: Pick<CInvoice, "effective_due_date" | "document_due_date">): string | null {
  return i.effective_due_date ?? i.document_due_date;
}

/** Konto in der Währung der Rechnung, sonst das Standardkonto. */
export function accountFor(accounts: readonly AgencyAccount[], currency: string): AgencyAccount | null {
  return accounts.find((a) => a.currency === currency && a.is_default) ?? accounts.find((a) => a.currency === currency) ?? accounts.find((a) => a.is_default) ?? accounts[0] ?? null;
}

export function ProvisionZahlen({ invoices, orgId, orgName, today, title }: { invoices: readonly CInvoice[]; orgId: string; orgName: string; today: string; title?: string }) {
  const t = useT();
  const lang = useLang();
  const open = invoices.filter(feeOpen);
  const accounts = useAgencyAccounts(orgId, open.length > 0);
  const { run, busy } = useAction();
  if (!open.length) return null;
  // je Währung getrennt summieren, nie EUR und CHF addieren
  const totals = open.reduce<Record<string, number>>((a, i) => ({ ...a, [i.currency]: (a[i.currency] ?? 0) + feeRest(i) }), {});
  return (
    <section className="panel stack" id="provision" aria-labelledby="provision-titel">
      <h2 id="provision-titel">{title ?? t("Du schuldest deiner Agentur", "You owe your agency")}</h2>
      <p>{t(`Du rechnest direkt mit der Marke ab; ${orgName} stellt dir ihre Provision in Rechnung. Diese Rechnungen bezahlst du selbst per Überweisung.`,
        `You bill the brand directly; ${orgName} invoices you its commission. You pay these invoices yourself by bank transfer.`)}</p>
      <ul className="tk-money-list">
        {open.map((i) => {
          const due = feeDue(i);
          const late = !!due && due < today;
          const acc = accountFor(accounts.data ?? [], i.currency);
          return (
            <li key={i.id} id={`provision-${i.id}`}>
              <span><strong>{i.number ?? t("Rechnung", "Invoice")}</strong> · {due ? t(`zahlbar bis ${dateOf(due, lang)}`, `payable by ${dateOf(due, lang)}`) : t("ohne Frist", "no due date")}</span>
              <span className="num"><strong>{money(feeRest(i), i.currency)}</strong>
                {feeRest(i) !== i.gross_cents && <small> {t(`von ${money(i.gross_cents, i.currency)}`, `of ${money(i.gross_cents, i.currency)}`)}</small>}</span>
              <small>
                {/* CREATOR-R3-6: gemeldete Überweisung läuft, bis die Agentur den Eingang erfasst */}
                <PillView pill={i.talent_payment_response === "paid" ? pill(t("Überwiesen gemeldet · Agentur bestätigt", "Transfer reported · agency confirms"), "info")
                  : late ? pill(t("Überfällig", "Overdue"), "error") : pill(t("Offen", "Open"), "warning")} />{" "}
                {i.pdf_path && <button type="button" onClick={() => void openDocument(i.pdf_path!, `${i.number ?? "rechnung"}.pdf`)}>{t("PDF öffnen", "Open PDF")}</button>}
                {i.talent_payment_response !== "paid" && !i.in_collection && (
                  <button type="button" disabled={busy === `transfer:${i.id}`} onClick={() => void run(`transfer:${i.id}`, () => reportTransfer(api, i.id),
                    t("Danke, deine Agentur sieht deine Überweisung und bestätigt den Eingang.", "Thanks, your agency sees your transfer and confirms receipt."))}>{t("Ich habe überwiesen", "I have transferred")}</button>
                )}
              </small>
              {acc ? (
                <dl className="tk-pay-details" style={{ gridColumn: "1 / -1" }}>
                  <dt>{t("Empfänger", "Payee")}</dt><dd>{acc.holder}</dd>
                  <dt>IBAN</dt><dd className="num" data-sensibel="">{acc.iban}</dd>
                  {acc.bic && <><dt>BIC</dt><dd className="num">{acc.bic}</dd></>}
                  {acc.bank_name && <><dt>{t("Bank", "Bank")}</dt><dd>{acc.bank_name}</dd></>}
                  <dt>{t("Verwendungszweck", "Reference")}</dt><dd className="num">{i.number ?? "–"}</dd>
                  <dt>{t("Betrag", "Amount")}</dt><dd className="num">{money(feeRest(i), i.currency)}</dd>
                </dl>
              ) : (
                <small>{accounts.isPending ? t("Zahlungsangaben werden geladen …", "Loading payment details …")
                  : t(`Die Bankverbindung steht auf der Rechnung; fehlt sie, frag ${orgName} im Chat.`, `The bank details are on the invoice; if they are missing, ask ${orgName} in the chat.`)}</small>
              )}
            </li>
          );
        })}
      </ul>
      {open.length > 1 && <p>{t("Zusammen", "Total")}: {Object.entries(totals).map(([cur, sum], n) => (
        <span key={cur}>{n > 0 && " · "}<strong className="num">{money(sum, cur)}</strong></span>))}</p>}
    </section>
  );
}
